import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { Sheet, SheetContent, SheetTitle, cn } from '@mlm/ui';
import { CashierSidebar } from './shell/sidebar';
import { OfflineBar } from './offline/offline-bar';
import { OfflineController } from './offline/offline-controller';
import { useIsLocalMode } from '../lib/offline/state';

/** Pages that work with no internet. Anywhere else goes back to the till. */
const worksOffline = (path: string) =>
  path === '/' ||
  path === '/checkout' ||
  path === '/end-shift' ||
  path === '/change-password' ||
  path.startsWith('/receipt/');

/**
 * Cashier shell. Icon rail on the left (or the labelled sidebar, for
 * back-office use), content column on the right; on narrow screens the sidebar
 * becomes a hamburger + sheet driven by the same nav data. The rail/labels
 * choice persists so the till keeps its preference across reloads (this is a
 * UI preference — the localStorage ban in CLAUDE.md is on TOKENS).
 *
 * PRINTING: the receipt tape is `position: absolute; left:0; top:0` under
 * `@media print` (index.css hides `body *` and un-hides `.receipt-tape`). Do
 * NOT put `transform`, `filter` or `overflow: hidden` on any ancestor of
 * <main> — any of those makes that ancestor the tape's containing block and
 * shifts the print off the page. Plain flex + sticky is safe.
 */

// A new key, not the old `…sidebar.collapsed`: that one was written on every
// load, so every existing till already holds "expanded" and would never have
// seen the rail become the default.
const RAIL_KEY = 'mlm.cashier.sidebar.rail';

export function Layout() {
  const { pathname } = useLocation();
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    // Default to the RAIL: the till is a touch screen first. Labels are one
    // tap away (the chevron at the foot of the rail) and the choice sticks.
    return window.localStorage.getItem(RAIL_KEY) !== '0';
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const flush = pathname === '/' || pathname === '/stockist';
  const local = useIsLocalMode();

  useEffect(() => {
    window.localStorage.setItem(RAIL_KEY, collapsed ? '1' : '0');
  }, [collapsed]);

  // Close the mobile drawer on navigation.
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  if (local && !worksOffline(pathname)) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen flex bg-muted/40 text-foreground">
      <OfflineController />
      <CashierSidebar
        collapsed={collapsed}
        onToggleCollapsed={() => setCollapsed((v) => !v)}
        className="hidden md:flex h-screen sticky top-0"
      />

      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-[240px]">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <CashierSidebar
            collapsed={false}
            onNavigate={() => setMobileOpen(false)}
            className="h-full w-full border-r-0"
          />
        </SheetContent>
      </Sheet>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="no-print md:hidden sticky top-0 z-10 border-b bg-background">
          <div className="flex h-14 items-center gap-3 px-4">
            <button
              type="button"
              aria-label="Open navigation"
              onClick={() => setMobileOpen(true)}
              className="flex h-11 w-11 items-center justify-center -ml-2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <Menu className="h-5 w-5" />
            </button>
            <span className="font-semibold text-sm">Cashier POS</span>
          </div>
        </header>
        {/* The till lays out its own two panes edge to edge (the cart is a
            full-height panel), so it gets no page padding. */}
        <OfflineBar />
        <main className={cn('flex-1', !flush && 'px-4 md:px-6 py-6')}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
